import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  api,
  ApiError,
  type ApiAdminCustomer,
  type ApiAdminOwner,
  type ApiAdminOwnerProject,
} from "../lib/api";
import { useAuth } from "../lib/auth";
import { ButtonSpinner, Skeleton, Spinner } from "../components/Loading";
import { ApiTokensPanel } from "../components/ApiTokensPanel";

// Owner-only admin: cross-org user management.
//
// Two populations on one page, with distinct tables because their data
// shapes differ — owners are Avista employees with `users` rows, customers
// are aggregated across `project_invitees` by email.

type LoadState<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "error"; message: string };

export function UsersAdminPage() {
  const navigate = useNavigate();
  const { state: authState, logout } = useAuth();
  const meId = authState.status === "authed" ? authState.user.id : null;

  const [owners, setOwners] = useState<LoadState<ApiAdminOwner[]>>({
    status: "loading",
  });
  const [customers, setCustomers] = useState<LoadState<ApiAdminCustomer[]>>({
    status: "loading",
  });

  const reloadOwners = useCallback(async () => {
    setOwners({ status: "loading" });
    try {
      const res = await api.adminListOwners();
      setOwners({ status: "ready", data: res.owners });
    } catch {
      setOwners({ status: "error", message: "Couldn’t load owners." });
    }
  }, []);

  const reloadCustomers = useCallback(async () => {
    setCustomers({ status: "loading" });
    try {
      const res = await api.adminListCustomers();
      setCustomers({ status: "ready", data: res.customers });
    } catch {
      setCustomers({ status: "error", message: "Couldn’t load collaborators." });
    }
  }, []);

  useEffect(() => {
    void reloadOwners();
    void reloadCustomers();
  }, [reloadOwners, reloadCustomers]);

  return (
    <section className="mx-auto max-w-5xl px-6 py-12">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Manage Avista owners, the collaborators who review your projects, and
            your own API tokens.
          </p>
        </div>
        <Link
          to="/dashboard"
          className="text-sm text-neutral-600 hover:text-neutral-900"
        >
          ← Back to projects
        </Link>
      </div>

      <OwnersSection
        load={owners}
        meId={meId}
        onChanged={reloadOwners}
        onSelfDeleted={() => {
          // Server already cleared our cookie. Reset client auth state so
          // the header flips back to anon, then send us home.
          void logout().finally(() => navigate("/", { replace: true }));
        }}
      />

      <CustomersSection load={customers} onChanged={reloadCustomers} />

      <ApiTokensPanel />
    </section>
  );
}

// ===========================================================================
// Owners
// ===========================================================================

function OwnersSection({
  load,
  meId,
  onChanged,
  onSelfDeleted,
}: {
  load: LoadState<ApiAdminOwner[]>;
  meId: string | null;
  onChanged: () => Promise<void>;
  onSelfDeleted: () => void;
}) {
  return (
    <div className="mt-10">
      <h2 className="text-lg font-semibold">
        Owners{" "}
        {load.status === "ready" ? (
          <span className="ml-1 text-sm font-normal text-neutral-500">
            ({load.data.length})
          </span>
        ) : null}
      </h2>
      <p className="mt-1 text-xs text-neutral-500">
        Avista employees with full access. New signups must use an{" "}
        <code>@avista.is</code> email.
      </p>

      <div className="mt-4 overflow-hidden rounded-lg border border-neutral-200 bg-white">
        {load.status === "loading" ? (
          <SkeletonTable
            columns={["Name", "Email", "Projects", "Joined", ""]}
            cells={["w-32", "w-48", "w-8", "w-16", "w-16"]}
          />
        ) : load.status === "error" ? (
          <p className="px-5 py-4 text-sm text-red-600">{load.message}</p>
        ) : load.data.length === 0 ? (
          <p className="px-5 py-4 text-sm text-neutral-500">No owners yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Projects</th>
                <th className="px-5 py-3 font-medium">Joined</th>
                <th className="px-5 py-3 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {load.data.map((owner) => (
                <OwnerRow
                  key={owner.id}
                  owner={owner}
                  isMe={owner.id === meId}
                  onChanged={onChanged}
                  onSelfDeleted={onSelfDeleted}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SkeletonTable({
  columns,
  cells,
  rows = 3,
}: {
  columns: string[];
  cells: string[];
  rows?: number;
}) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
        <tr>
          {columns.map((c, i) => (
            <th key={i} className="px-5 py-3 font-medium">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-neutral-100">
        {Array.from({ length: rows }).map((_, r) => (
          <tr key={r}>
            {cells.map((w, i) => (
              <td key={i} className="px-5 py-3">
                <Skeleton className={`h-3 ${w}`} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OwnerRow({
  owner,
  isMe,
  onChanged,
  onSelfDeleted,
}: {
  owner: ApiAdminOwner;
  isMe: boolean;
  onChanged: () => Promise<void>;
  onSelfDeleted: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(owner.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProjects, setShowProjects] = useState(false);

  async function saveName() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    try {
      await api.adminUpdateOwner(owner.id, { name: trimmed });
      setEditing(false);
      await onChanged();
    } catch {
      setError("Couldn’t save. Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    const ok = window.confirm(
      isMe
        ? `Delete your own account (${owner.email})? You'll be signed out immediately.`
        : `Delete ${owner.email}? This cannot be undone.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.adminDeleteOwner(owner.id);
      if (res.deletedSelf) {
        onSelfDeleted();
        return;
      }
      await onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as {
          error?: string;
          message?: string;
          projectCount?: number;
        };
        setError(
          body.message ??
            (body.error === "last_owner"
              ? "Can't delete the last owner account."
              : "Refused: this owner has projects."),
        );
      } else {
        setError("Couldn’t delete. Try again.");
      }
    } finally {
      setBusy(false);
    }
  }

  const joined = new Date(owner.createdAt).toLocaleDateString();

  return (
    <>
    <tr>
      <td className="px-5 py-3 align-middle">
        {editing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
              className="rounded-md border border-neutral-300 px-2 py-1 text-sm focus:border-neutral-900 focus:outline-none"
              autoFocus
            />
            <button
              type="button"
              onClick={() => void saveName()}
              disabled={busy || !name.trim()}
              className="rounded-md bg-neutral-900 px-2 py-1 text-xs text-white hover:bg-neutral-700 disabled:opacity-50"
            >
              {busy ? <ButtonSpinner label="Saving" /> : "Save"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setName(owner.name);
                setError(null);
              }}
              disabled={busy}
              className="text-xs text-neutral-500 hover:text-neutral-900"
            >
              Cancel
            </button>
          </div>
        ) : (
          <span className="font-medium text-neutral-900">
            {owner.name}
            {isMe ? (
              <span className="ml-2 text-xs font-normal text-neutral-500">
                (you)
              </span>
            ) : null}
          </span>
        )}
        {error ? (
          <p className="mt-1 text-xs text-red-600">{error}</p>
        ) : null}
      </td>
      <td className="px-5 py-3 align-middle font-mono text-xs text-neutral-700">
        {owner.email}
      </td>
      <td className="px-5 py-3 align-middle text-neutral-700">
        {owner.projectCount > 0 ? (
          <button
            type="button"
            onClick={() => setShowProjects((v) => !v)}
            className="inline-flex items-center gap-1 text-xs text-neutral-700 hover:text-neutral-900"
            aria-expanded={showProjects}
          >
            <span>{owner.projectCount}</span>
            <span className="text-neutral-500">
              · {showProjects ? "Hide" : "View"}
            </span>
          </button>
        ) : (
          <span className="text-xs text-neutral-400">0</span>
        )}
      </td>
      <td className="px-5 py-3 align-middle text-xs text-neutral-500">
        {joined}
      </td>
      <td className="px-5 py-3 text-right align-middle">
        {editing ? null : (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(true)}
              disabled={busy}
              className="text-xs text-neutral-600 hover:text-neutral-900"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => void onDelete()}
              disabled={busy}
              className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              {busy ? <Spinner size={12} /> : null}
              Delete
            </button>
          </div>
        )}
      </td>
    </tr>
    {showProjects ? (
      <tr className="bg-neutral-50/50">
        <td colSpan={5} className="px-5 py-3">
          <OwnerProjectsPanel
            ownerId={owner.id}
            ownerEmail={owner.email}
            onProjectDeleted={onChanged}
          />
        </td>
      </tr>
    ) : null}
    </>
  );
}

function OwnerProjectsPanel({
  ownerId,
  ownerEmail,
  onProjectDeleted,
}: {
  ownerId: string;
  ownerEmail: string;
  onProjectDeleted: () => Promise<void>;
}) {
  const [load, setLoad] = useState<LoadState<ApiAdminOwnerProject[]>>({
    status: "loading",
  });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoad({ status: "loading" });
    try {
      const res = await api.adminListOwnerProjects(ownerId);
      setLoad({ status: "ready", data: res.projects });
    } catch {
      setLoad({ status: "error", message: "Couldn’t load projects." });
    }
  }, [ownerId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function onDelete(p: ApiAdminOwnerProject) {
    const ok = window.confirm(
      `Delete "${p.name}" (owned by ${ownerEmail})? This permanently removes the project, its pages, comments, and invitee assignments.`,
    );
    if (!ok) return;
    setBusyId(p.id);
    setError(null);
    try {
      await api.adminDeleteProject(p.id);
      // Refresh both the embedded list and the outer owner row's count.
      await Promise.all([reload(), onProjectDeleted()]);
    } catch {
      setError(`Couldn’t delete "${p.name}". Try again.`);
    } finally {
      setBusyId(null);
    }
  }

  if (load.status === "loading") {
    return (
      <div className="space-y-2">
        <Skeleton className="h-3 w-48" />
        <Skeleton className="h-3 w-64" />
      </div>
    );
  }
  if (load.status === "error") {
    return <p className="text-xs text-red-600">{load.message}</p>;
  }
  if (load.data.length === 0) {
    return (
      <p className="text-xs text-neutral-500">
        No projects owned by this account.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-[11px] uppercase tracking-wide text-neutral-500">
        Projects owned by {ownerEmail}
      </p>
      <ul className="divide-y divide-neutral-200 overflow-hidden rounded-md border border-neutral-200 bg-white">
        {load.data.map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-between gap-3 px-3 py-2 text-xs"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-neutral-900">
                  {p.name}
                </span>
                {p.archivedAt ? (
                  <span className="rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-neutral-500">
                    archived
                  </span>
                ) : null}
              </div>
              <p className="mt-0.5 truncate text-neutral-500">{p.targetUrl}</p>
            </div>
            <button
              type="button"
              onClick={() => void onDelete(p)}
              disabled={busyId !== null}
              className="inline-flex shrink-0 items-center gap-1 text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              {busyId === p.id ? <Spinner size={12} /> : null}
              Delete project
            </button>
          </li>
        ))}
      </ul>
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

// ===========================================================================
// Customers
// ===========================================================================

function CustomersSection({
  load,
  onChanged,
}: {
  load: LoadState<ApiAdminCustomer[]>;
  onChanged: () => Promise<void>;
}) {
  return (
    <div className="mt-12">
      <h2 className="text-lg font-semibold">
        Collaborators{" "}
        {load.status === "ready" ? (
          <span className="ml-1 text-sm font-normal text-neutral-500">
            ({load.data.length})
          </span>
        ) : null}
      </h2>
      <p className="mt-1 text-xs text-neutral-500">
        Reviewers invited to one or more projects. Revoking ends their access
        everywhere; their past comments stay on the projects.
      </p>

      <div className="mt-4 overflow-hidden rounded-lg border border-neutral-200 bg-white">
        {load.status === "loading" ? (
          <SkeletonTable
            columns={["Email", "Display name", "Projects", "Last seen", ""]}
            cells={["w-48", "w-32", "w-24", "w-16", "w-24"]}
          />
        ) : load.status === "error" ? (
          <p className="px-5 py-4 text-sm text-red-600">{load.message}</p>
        ) : load.data.length === 0 ? (
          <p className="px-5 py-4 text-sm text-neutral-500">No collaborators yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium">Display name</th>
                <th className="px-5 py-3 font-medium">Projects</th>
                <th className="px-5 py-3 font-medium">Last seen</th>
                <th className="px-5 py-3 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {load.data.map((c) => (
                <CustomerRow key={c.email} customer={c} onChanged={onChanged} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CustomerRow({
  customer,
  onChanged,
}: {
  customer: ApiAdminCustomer;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const status = customer.activeCount > 0 ? "active" : "revoked";

  async function onRevoke() {
    const ok = window.confirm(
      `Revoke ${customer.email} from all ${customer.activeCount} active project${
        customer.activeCount === 1 ? "" : "s"
      }? They'll be signed out and lose access immediately. Past comments are preserved.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await api.adminRevokeCustomer(customer.email);
      await onChanged();
    } catch {
      setError("Couldn’t revoke. Try again.");
    } finally {
      setBusy(false);
    }
  }

  const lastSeen = customer.lastSeenAt
    ? new Date(customer.lastSeenAt).toLocaleDateString()
    : "—";

  return (
    <tr>
      <td className="px-5 py-3 align-middle font-mono text-xs text-neutral-700">
        {customer.email}
      </td>
      <td className="px-5 py-3 align-middle">
        <span className="text-neutral-900">{customer.name}</span>
        {status === "active" ? null : (
          <span className="ml-2 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] uppercase tracking-wide text-neutral-500">
            revoked
          </span>
        )}
        {error ? (
          <p className="mt-1 text-xs text-red-600">{error}</p>
        ) : null}
      </td>
      <td className="px-5 py-3 align-middle text-xs text-neutral-700">
        {customer.activeCount} active
        {customer.revokedCount > 0 ? (
          <span className="text-neutral-400">
            {" "}
            · {customer.revokedCount} revoked
          </span>
        ) : null}
      </td>
      <td className="px-5 py-3 align-middle text-xs text-neutral-500">
        {lastSeen}
      </td>
      <td className="px-5 py-3 text-right align-middle">
        {status === "active" ? (
          <button
            type="button"
            onClick={() => void onRevoke()}
            disabled={busy}
            className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
          >
            {busy ? <Spinner size={12} /> : null}
            Revoke access
          </button>
        ) : (
          <span className="text-xs text-neutral-400">No access</span>
        )}
      </td>
    </tr>
  );
}
